import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
}

interface PaginatedResponse {
  data: User[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
}

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'trainer' });
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get<PaginatedResponse>(`/admin/users?page=${page}&limit=20`);
      // The API returns {success, data, pagination} but our client unwraps to the data field
      // For paginated responses, we need the full response
      setUsers(Array.isArray(res) ? res : (res as any).data || (res as any));
      setPagination((res as any).pagination || { total: 0, page: 1, total_pages: 1 });
    } catch {
      // Fallback: try direct
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/users', form);
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'trainer' });
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
    setCreating(false);
  };

  const handleDelete = async (userId: string) => {
    try {
      await api.delete<void>(`/admin/users/${userId}`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const roleColors: Record<string, string> = {
    candidate: 'bg-blue-100 text-blue-700',
    trainer: 'bg-green-100 text-green-700',
    admin: 'bg-purple-100 text-purple-700',
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-mustard-green-dark">User Management</h1>
            <p className="text-mustard-charcoal/60 mt-1">{pagination.total} users total</p>
          </div>
          <button onClick={() => setShowCreate(!showCreate)} className="px-4 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium hover:bg-mustard-green transition-colors">
            {showCreate ? 'Cancel' : 'Create User'}
          </button>
        </div>

        {/* Create User Form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Create Trainer or Admin</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required />
              <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required />
              <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-4 py-2 border rounded-lg text-sm">
                <option value="trainer">Trainer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" disabled={creating} className="mt-4 px-6 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </form>
        )}

        {/* Users Table */}
        {loading ? (
          <p className="text-mustard-charcoal/60">Loading users...</p>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Role</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Joined</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-mustard-charcoal">{user.name}</td>
                    <td className="px-6 py-4 text-sm text-mustard-charcoal/60">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${roleColors[user.role] || 'bg-gray-100'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-mustard-charcoal/50">
                      {new Date(user.created_at).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {deleteConfirm === user.id ? (
                        <span className="text-xs">
                          <button onClick={() => handleDelete(user.id)} className="text-red-600 font-medium mr-2">Confirm Delete</button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-gray-500">Cancel</button>
                        </span>
                      ) : user.role !== 'admin' ? (
                        <button onClick={() => setDeleteConfirm(user.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
